import { CatalogKind, MediumChannel, SubStatusGroup } from '@performance-crm/shared';
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import {
  useAdminCatalogs,
  useUpsertCatalogItem,
  useUpsertMedium,
  useUpsertMediumCategory,
} from '@/lib/queries';

const KIND_LABEL: Record<CatalogKind, string> = {
  ENTRY_CHANNEL: 'Canales de entrada',
  SUB_STATUS: 'Sub-estados',
  DROP_REASON: 'Motivos de caída (procesos)',
  DESIST_REASON: 'Motivos de desistimiento',
  NON_EFFECTIVE_REASON: 'Motivos de contacto no efectivo',
  UNIT_TYPOLOGY: 'Tipologías de unidad',
};

/** Catálogos: ítems por tipo, categorías de medio (canal + digital) y medio → categoría. Desactivar en vez de borrar. */
export function CatalogsTab({ companyId }: { companyId: string }) {
  const { data, isLoading } = useAdminCatalogs(companyId);
  // Desde Admin → Empresa → "Agregar un medio de captación" se llega con #medios: bajar hasta la tarjeta, pero
  // recién cuando los datos cargaron (antes solo existe el esqueleto y no hay a dónde bajar; ux-admin-27).
  // Una sola vez: el hash sigue en la URL y cada guardado recarga `data`, pero la pantalla no debe volver a saltar.
  const scrolledToMedia = useRef(false);
  useEffect(() => {
    if (scrolledToMedia.current || isLoading || !data || window.location.hash !== '#medios') return;
    scrolledToMedia.current = true;
    document.getElementById('medios')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isLoading, data]);
  const upsertItem = useUpsertCatalogItem();
  const upsertCategory = useUpsertMediumCategory();
  const upsertMedium = useUpsertMedium();
  const toast = useToast();
  const [kind, setKind] = useState<CatalogKind>('SUB_STATUS');
  const [item, setItem] = useState({ code: '', label: '', group: '', technical: false });
  const [cat, setCat] = useState({
    code: '',
    name: '',
    channel: 'DIGITAL' as MediumChannel,
    isDigital: true,
  });
  const [medium, setMedium] = useState({ name: '', categoryCode: '' });
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  const items = data.items.filter((i) => i.kind === kind);
  const submitItem = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await upsertItem.mutateAsync({
        companyId,
        kind,
        code: item.code.toUpperCase().replace(/\s+/g, '_'),
        label: item.label,
        group: kind === 'SUB_STATUS' ? item.group || null : null,
        metadata: kind === 'DROP_REASON' ? { isTechnical: item.technical } : undefined,
      });
      toast.success('Ítem guardado');
      setItem({ code: '', label: '', group: '', technical: false });
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  const toggleItem = (i: (typeof data.items)[number]) =>
    upsertItem
      .mutateAsync({
        companyId,
        kind: i.kind,
        code: i.code,
        label: i.label,
        group: i.group,
        sortOrder: i.sortOrder,
        metadata: i.metadata,
        active: !i.active,
      })
      .catch((err: Error) => toast.error('No se pudo actualizar', err.message));
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Ítems de catálogo</CardTitle>
          <CardDescription>
            Los códigos son estables (se usan en reglas y reportes); las etiquetas se pueden
            cambiar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value as CatalogKind)}
            aria-label="Tipo de catálogo"
          >
            {Object.values(CatalogKind).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </Select>
          <ul className="max-h-72 space-y-1 overflow-y-auto">
            {items.map((i) => (
              <li
                key={i.id}
                className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 text-sm ${!i.active ? 'opacity-50' : ''}`}
                data-testid="catalog-item"
              >
                <span>
                  <span className="font-mono text-xs">{i.code}</span> · {i.label}
                  {i.group && (
                    <Badge tone="outline" className="ml-1">
                      {i.group}
                    </Badge>
                  )}
                  {i.metadata.isTechnical === true && (
                    <Badge tone="danger" className="ml-1">
                      técnico
                    </Badge>
                  )}
                </span>
                <button
                  type="button"
                  className="text-primary text-xs underline"
                  onClick={() => void toggleItem(i)}
                >
                  {i.active ? 'desactivar' : 'activar'}
                </button>
              </li>
            ))}
            {items.length === 0 && <li className="text-muted-foreground text-sm">Sin ítems.</li>}
          </ul>
          <form
            onSubmit={submitItem}
            className="grid gap-2 border-t pt-3 sm:grid-cols-[1fr_2fr_auto]"
          >
            <div className="space-y-1">
              <Label htmlFor="ci-code">Código</Label>
              <Input
                id="ci-code"
                value={item.code}
                onChange={(e) => setItem({ ...item, code: e.target.value })}
                required
                placeholder="MUDANZA"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ci-label">Etiqueta</Label>
              <Input
                id="ci-label"
                value={item.label}
                onChange={(e) => setItem({ ...item, label: e.target.value })}
                required
                placeholder="Se mudó de ciudad"
              />
            </div>
            <Button
              type="submit"
              className="self-end"
              disabled={upsertItem.isPending}
              data-testid="catalog-item-save"
            >
              <Plus className="h-4 w-4" />
            </Button>
            {kind === 'SUB_STATUS' && (
              <div className="space-y-1 sm:col-span-3">
                <Label htmlFor="ci-group">Agrupación</Label>
                <Select
                  id="ci-group"
                  value={item.group}
                  onChange={(e) => setItem({ ...item, group: e.target.value })}
                  required
                >
                  <option value="">Selecciona…</option>
                  {Object.values(SubStatusGroup).map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {kind === 'DROP_REASON' && (
              <label className="flex items-center gap-2 text-sm sm:col-span-3">
                <input
                  type="checkbox"
                  checked={item.technical}
                  onChange={(e) => setItem({ ...item, technical: e.target.checked })}
                  className="h-4 w-4"
                />
                Motivo técnico (excluye el proceso de las métricas)
              </label>
            )}
          </form>
        </CardContent>
      </Card>
      <Card id="medios">
        <CardHeader>
          <CardTitle>Medios y categorías</CardTitle>
          <CardDescription>
            Cada medio de captación se mapea a una categoría; la categoría define canal y si es
            digital.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ul className="max-h-48 space-y-1 overflow-y-auto">
            {data.mediumCategories.map((c) => (
              <li
                key={c.id}
                className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 text-sm ${!c.active ? 'opacity-50' : ''}`}
                data-testid="medium-category"
              >
                <span>
                  <span className="font-mono text-xs">{c.code}</span> · {c.name}
                  <Badge tone="outline" className="ml-1">
                    {c.channel}
                  </Badge>
                  {c.isDigital && (
                    <Badge tone="primary" className="ml-1">
                      digital
                    </Badge>
                  )}
                  <span className="text-muted-foreground ml-1 text-xs">{c.media} medios</span>
                </span>
                <button
                  type="button"
                  className="text-primary text-xs underline"
                  onClick={() =>
                    upsertCategory
                      .mutateAsync({
                        companyId,
                        code: c.code,
                        name: c.name,
                        channel: c.channel,
                        isDigital: c.isDigital,
                        sortOrder: c.sortOrder,
                        active: !c.active,
                      })
                      .catch((err: Error) => toast.error('No se pudo actualizar', err.message))
                  }
                >
                  {c.active ? 'desactivar' : 'activar'}
                </button>
              </li>
            ))}
          </ul>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              upsertCategory
                .mutateAsync({
                  companyId,
                  code: cat.code.toUpperCase().replace(/\s+/g, '_'),
                  name: cat.name,
                  channel: cat.channel,
                  isDigital: cat.isDigital,
                })
                .then(() => {
                  toast.success('Categoría guardada');
                  setCat({ code: '', name: '', channel: 'DIGITAL', isDigital: true });
                })
                .catch((err: Error) => toast.error('No se pudo guardar', err.message));
            }}
            className="grid gap-2 border-t pt-3 sm:grid-cols-4"
          >
            <Input
              value={cat.code}
              onChange={(e) => setCat({ ...cat, code: e.target.value })}
              required
              placeholder="Código"
              aria-label="Código de categoría"
            />
            <Input
              value={cat.name}
              onChange={(e) => setCat({ ...cat, name: e.target.value })}
              required
              placeholder="Nombre"
              aria-label="Nombre de categoría"
            />
            <Select
              value={cat.channel}
              onChange={(e) => setCat({ ...cat, channel: e.target.value as MediumChannel })}
              aria-label="Canal"
            >
              {Object.values(MediumChannel).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs">
                <input
                  type="checkbox"
                  checked={cat.isDigital}
                  onChange={(e) => setCat({ ...cat, isDigital: e.target.checked })}
                  className="h-4 w-4"
                />{' '}
                digital
              </label>
              <Button
                type="submit"
                size="icon"
                disabled={upsertCategory.isPending}
                aria-label="Guardar categoría"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </form>
          <div className="border-t pt-3">
            <p className="text-muted-foreground mb-1 text-xs uppercase">Medio → categoría</p>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
              {data.media.map((m) => (
                <li
                  key={m.id}
                  className={`flex justify-between rounded border px-2 py-1 ${!m.active ? 'opacity-50' : ''}`}
                >
                  <span>{m.name}</span>
                  <span className="text-muted-foreground font-mono text-xs">{m.categoryCode}</span>
                </li>
              ))}
            </ul>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                upsertMedium
                  .mutateAsync({ companyId, name: medium.name, categoryCode: medium.categoryCode })
                  .then(() => {
                    toast.success('Medio guardado');
                    setMedium({ name: '', categoryCode: '' });
                  })
                  .catch((err: Error) => toast.error('No se pudo guardar', err.message));
              }}
              className="mt-2 grid gap-2 sm:grid-cols-[2fr_2fr_auto]"
            >
              <Input
                value={medium.name}
                onChange={(e) => setMedium({ ...medium, name: e.target.value })}
                required
                placeholder="Nuevo medio (p. ej. THREADS)"
                aria-label="Nombre del medio"
              />
              <Select
                value={medium.categoryCode}
                onChange={(e) => setMedium({ ...medium, categoryCode: e.target.value })}
                required
                aria-label="Categoría del medio"
              >
                <option value="">Categoría…</option>
                {data.mediumCategories
                  .filter((c) => c.active)
                  .map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
              </Select>
              <Button
                type="submit"
                size="icon"
                disabled={upsertMedium.isPending}
                aria-label="Guardar medio"
                data-testid="medium-save"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
